<template>
  <div class="depositos-container">

    <!-- ================= HEADER ================= -->
    <div class="page-header">
      <div class="d-flex flex-column">
        <div class="d-flex align-center flex-wrap">
          <div class="d-flex align-center">
            <v-avatar size="48" color="blue-darken-1" class="mr-3" variant="tonal">
              <v-icon color="white" size="28">mdi-warehouse</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold text-grey-darken-4 mb-1">Consulta de Depósitos</h1>
              <div class="text-caption text-grey">
                Listagem de depósitos cadastrados
              </div>
            </div>
          </div>
          
          <v-spacer />
          
          <div class="d-flex align-center mt-2 mt-sm-0">
            <v-btn
              color="grey-darken-1"
              variant="text"
              @click="voltarMenu"
              class="text-none mr-2"
              prepend-icon="mdi-arrow-left"
            >
              Voltar
            </v-btn>
          
          </div>
        </div>
      </div>
    </div>

    <!-- ================= FILTROS EXPANSÍVEIS ================= -->
    <v-card class="filters-card mb-4" elevation="2">
      <v-card-title class="filters-header pa-4">
        <div class="d-flex align-center w-100">
          <!-- Seção Esquerda - Título e Info -->
          <div class="d-flex align-center cursor-pointer flex-grow-1" @click="toggleFilters">
            <v-icon class="mr-2" color="white">mdi-filter-variant</v-icon>
            <span class="filters-title">Filtros</span>
            
            <div class="d-flex align-center gap-2 ml-4">
              <v-chip variant="tonal" size="small">
                {{ filteredDepositos.length }} depósitos
              </v-chip>
              
              <v-chip 
                v-if="search"
                color="warning" 
                variant="tonal" 
                size="small"
              >
                1 filtro(s)
              </v-chip>
            </div>
          </div>
          
          <!-- Seção Central - Botões de Ação -->
          <div class="d-flex align-center gap-2 mr-2">
            <v-btn
              color="white"
              variant="flat"
              size="small"
              @click="fetchData"
            >
              <v-icon size="18" color="grey-darken-2">mdi-refresh</v-icon>
              <span class="ml-2 text-grey-darken-2">Atualizar</span>
            </v-btn>
            
            <v-btn
              color="white"
              class="ml-5"
              variant="flat"
              size="small"
              @click="search = ''"
              :disabled="!search"
            >
              <v-icon size="18" :color="search ? 'grey-darken-2' : 'grey-lighten-1'">mdi-filter-off</v-icon>
              <span class="ml-2" :class="search ? 'text-grey-darken-2' : 'text-grey-lighten-1'">Limpar</span>
            </v-btn>
          </div>
          
          <!-- Botão Expandir/Recolher -->
          <v-btn
            icon
            variant="text"
            size="small"
            color="white"
            @click="toggleFilters"
          >
            <v-icon size="20" :class="{ 'rotate-180': filtersExpanded }">
              mdi-chevron-down
            </v-icon>
            <v-tooltip activator="parent">
              {{ filtersExpanded ? 'Recolher Filtros' : 'Expandir Filtros' }}
            </v-tooltip>
          </v-btn>
        </div>
      </v-card-title>
      
      <!-- Conteúdo Expansível -->
      <v-expand-transition>
        <div v-show="filtersExpanded">
          <v-divider />
          <v-card-text class="pa-4">
            <v-text-field
              v-model="search"
              label="Buscar depósito"
              placeholder="Código ou nome do depósito"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              clearable
              hide-details
              density="compact"
            />
          </v-card-text>imit
        </div>
      </v-expand-transition>
    </v-card>

    <!-- ================= GRID / TABELA ================= -->
    <div class="table-layout">
      <!-- COLUNA ESQUERDA - DEPÓSITOS -->
      <v-card class="table-card" elevation="2">
        <v-card-title class="pa-4 bg-blue-darken-1 text-white">
          <div class="d-flex align-center w-100">
            <v-icon class="mr-2">mdi-warehouse</v-icon>
            <span class="text-h6">Depósitos</span>
            <v-spacer />
            <v-chip 
              variant="tonal" 
              size="small" 
              color="white"
            >
              {{ depositos.length }} depósitos
            </v-chip>
          </div>
        </v-card-title>
        
        <v-data-table
          :headers="headers"
          :items="filteredDepositos"
          item-key="id_deposito"
          class="scrollable-table"
          density="compact"
          fixed-header
          height="calc(100vh - 280px)"
          :items-per-page="25"
          :items-per-page-options="[10, 25, 50, 100]"
          @click:row="selecionarDeposito"
        >
          <!-- ID -->
          <template #item.id_deposito="{ item }">
            <v-chip
              :color="selectedDeposito?.id_deposito === item.id_deposito ? 'primary' : 'blue-darken-1'"
              variant="tonal"
              size="small"
            >
              {{ item.id_deposito }}
            </v-chip>
          </template>

          <!-- Nome -->
          <template #item.nome_deposito="{ item }">
            <div class="text-truncate" style="max-width: 240px">
              <strong :class="selectedDeposito?.id_deposito === item.id_deposito ? 'text-primary' : ''">
                {{ item.nome_deposito }}
              </strong>
            </div>
          </template>

          <!-- Status -->
          <template #item.inativo="{ item }">
            <v-chip
              :color="item.inativo === 'N' ? 'success' : 'error'"
              variant="tonal"
              size="small"
            >
              {{ item.inativo === 'N' ? 'Ativo' : 'Inativo' }}
            </v-chip>
          </template>

          <!-- Ações -->
          <template #item.acoes="{ item }">
            <v-btn
              icon="mdi-eye"
              size="small"
              variant="text"
              color="primary"
              @click.stop="selecionarDeposito(null, { item })"
            >
              <v-tooltip activator="parent">
                Ver endereços
              </v-tooltip>
            </v-btn>
          </template>

          <!-- Nenhum resultado -->
          <template #no-data>
            <div class="text-center pa-6 text-grey">
              Nenhum depósito encontrado
            </div>
          </template>
        </v-data-table>
      </v-card>

      <!-- COLUNA DIREITA - ENDEREÇOS -->
      <v-card class="enderecos-card" elevation="2">
        <v-card-title class="pa-4 bg-orange-darken-1 text-white">
          <div class="d-flex align-center w-100">
            <v-icon class="mr-2">mdi-map-marker-multiple</v-icon>
            <span class="text-h6">Endereços</span>
            <v-spacer />
            <v-chip 
              v-if="selectedDeposito"
              variant="tonal" 
              size="small" 
              color="white"
            >
              {{ selectedDeposito.enderecos?.length || 0 }} endereços
            </v-chip>
          </div>
        </v-card-title>
        
        <v-card-text class="pa-0">
          <!-- SEM DEPÓSITO SELECIONADO -->
          <div v-if="!selectedDeposito" class="text-center pa-8 text-grey">
            <v-icon size="48" color="grey-lighten-1">mdi-cursor-default-click</v-icon>
            <p class="mt-4">Selecione um depósito para visualizar os endereços</p>
          </div>
          
          <!-- LISTA DE ENDEREÇOS -->
          <div v-else-if="selectedDeposito.enderecos && selectedDeposito.enderecos.length > 0">
            <v-list density="compact" class="enderecos-list">
              <v-list-item
                v-for="endereco in selectedDeposito.enderecos"
                :key="endereco.id_localizacao"
                class="endereco-item"
              >
                <template #prepend>
                  <v-icon color="orange-darken-1" class="mr-2">
                    mdi-map-marker
                  </v-icon>
                </template>
                
                <v-list-item-title class="font-weight-medium">
                  {{ endereco.localizacao }}
                </v-list-item-title>
                
                <v-list-item-subtitle class="text-caption">
                  <v-chip variant="tonal" size="x-small" color="grey" class="mr-1">
                    {{ endereco.endereco }}
                  </v-chip>
                  <v-chip 
                    v-if="endereco.prateleira" 
                    variant="tonal" 
                    size="x-small" 
                    color="blue-grey"
                  >
                    Prateleira {{ endereco.prateleira }}
                  </v-chip>
                </v-list-item-subtitle>
                
                <template #append>
                  <v-chip variant="tonal" size="small" color="primary">
                    #{{ endereco.id_localizacao }}
                  </v-chip>
                </template>
              </v-list-item>
            </v-list>
          </div>
          
          <!-- SEM ENDEREÇOS -->
          <div v-else class="text-center pa-8 text-grey">
            <v-icon size="48" color="grey-lighten-1">mdi-map-marker-off</v-icon>
            <p class="mt-4">Nenhum endereço encontrado para este depósito</p>
          </div>
        </v-card-text>
      </v-card>
    </div>

  </div>
</template>

<script>
import { sapeJodarcService } from "@/services/sapeJodarcService";
import { useToast } from "vue-toastification";

const toast = useToast();

export default {
  name: "ConsultaDepositos",

  data() {
    return {
      depositos: [],
      search: "",
      filtersExpanded: false,

      headers: [
        {
          title: "ID",
          key: "id_deposito",
          width: "120px",
        },
        {
          title: "Depósito",
          key: "nome_deposito",
        },
        {
          title: "Status",
          key: "inativo",
          width: "140px",
        },
        {
          title: "Ações",
          key: "acoes",
          width: "80px",
          sortable: false,
        },
      ],
      selectedDeposito: null,
    };
  },

  computed: {
    filteredDepositos() {
      const termo = this.search.toLowerCase().trim();

      return this.depositos.filter(dep => {
        if (!termo) return true;

        return (
          String(dep.id_deposito).toLowerCase().includes(termo) ||
          String(dep.nome_deposito).toLowerCase().includes(termo)
        );
      });
    },
  },

  methods: {
    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
    },

    async fetchData() {
      try {
        const response = await sapeJodarcService.sapeDeposito({});

        if (response?.success) {
          this.depositos = Array.isArray(response.data)
            ? response.data
            : [];
        } else {
          this.depositos = [];
          toast.error(response?.message || "Erro ao carregar depósitos");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao carregar depósitos");
        this.depositos = [];
      }
    },

    selecionarDeposito(event, { item }) {
      this.selectedDeposito = item;
    },

    voltarMenu() {
      this.$router.push({ name: "Menu" });
    },
  },

  mounted() {
    this.fetchData();
  },
};
</script>

<style scoped>
/* ✅ CONTAINER PRINCIPAL */
.depositos-container {
  padding: 24px;
  background: #e4edfb;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 16px;
}

/* ✅ ESTILOS DOS FILTROS */
.filters-card {
  border-radius: 12px;
  overflow: hidden;
}

.filters-header {
  background: linear-gradient(135deg, #1976d2, #1565c0);
  color: white;
}

.filters-title {
  font-size: 1rem;
  font-weight: 600;
}

/* ✅ ROTAÇÃO DO ÍCONE EXPANDIR */
.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

.cursor-pointer {
  cursor: pointer;
}

/* ✅ LAYOUT DUAS COLUNAS */
.table-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  height: calc(100vh - 280px);
}

/* ✅ ESTILOS DA TABELA */
.table-card {
  border-radius: 12px;
  overflow: hidden;
}

/* ✅ ESTILOS DO CARD DE ENDEREÇOS */
.enderecos-card {
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.enderecos-list {
  height: calc(100vh - 350px);
  overflow-y: auto;
}

.enderecos-list::-webkit-scrollbar {
  width: 6px;
}

.enderecos-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.enderecos-list::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #f57c00, #ff9800);
  border-radius: 3px;
}

.endereco-item {
  border-bottom: 1px solid #f5f5f5;
}

.endereco-item:hover {
  background-color: rgba(245, 124, 0, 0.05);
}

.endereco-item:last-child {
  border-bottom: none;
}

/* ✅ SCROLL CUSTOMIZADO PARA TABELA */
.scrollable-table :deep(.v-table__wrapper) {
  overflow-x: auto !important;
  overflow-y: auto !important;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #1976d2, #2196f3);
  border-radius: 4px;
}

/* ✅ HEADERS DA TABELA */
.scrollable-table :deep(.v-data-table__th) {
  font-weight: 600 !important;
  background-color: #f8f9fa !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 10 !important;
}

/* ✅ COLUNA ID FIXA */
.scrollable-table :deep(.v-data-table__th:first-child),
.scrollable-table :deep(.v-data-table__td:first-child) {
  position: sticky !important;
  left: 0 !important;
  z-index: 5 !important;
  background: white !important;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1) !important;
}

.scrollable-table :deep(.v-data-table__th:first-child) {
  z-index: 15 !important;
  background: #f8f9fa !important;
}

/* ✅ HOVER DA LINHA */
.scrollable-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(25, 118, 210, 0.05) !important;
}

.scrollable-table :deep(.v-data-table__tr:hover .v-data-table__td:first-child) {
  background-color: rgba(25, 118, 210, 0.05) !important;
}

/* ✅ TRUNCATE */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ✅ RESPONSIVIDADE */
@media (max-width: 1200px) {
  .table-layout {
    grid-template-columns: 2fr 1fr;
  }
}

@media (max-width: 768px) {
  .depositos-container {
    padding: 16px;
  }
  
  .table-layout {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .scrollable-table :deep(.v-data-table__th),
  .scrollable-table :deep(.v-data-table__td) {
    font-size: 0.875rem;
  }
  
  .enderecos-list {
    height: 300px;
  }
}
</style>
